/**
 * @module  public/js/services/loginService.js
 * @summary Centralised authentication helper that signs users in and caches CRM connection data.
 *
 * @description
 * Wraps Firebase email-password auth and Firestore lookups to expose simple login utilities.
 * Also fetches HubSpot owner data and example account lists through an EndpointService bridge.
 *
 * ## Key Features
 * – One-call `signInUser()` for Firebase email authentication
 * – Reads and caches per-user CRM connection flags in sessionStorage
 * – Fetches HubSpot owner ID and sample account data from the backend
 * – Convenience renderer for account lists
 *
 * ## Public API
 * – `signInUser(email, password)` — Firebase email/password sign-in
 * – `storeConnectionStatus(userId)` — persist HubSpot/Salesforce flags to sessionStorage
 * – `fetchHubspotOwnerId(userId)` — returns HubSpot owner ID string or `null`
 * – `fetchAccountsFromBackend(ownerId)` — retrieves account array via endpoints service
 * – `renderAccounts(accounts, containerId)` — simple `<ul>` renderer into DOM
 *
 * ## Dependencies
 * { Firebase Auth, Firebase Firestore, endpoints-service }
 *
 * @author   Auto-Generated
 * @version  1.0.0
 * @since    2025-04-28
 */

import { auth, authReady } from '../core/firebase-service.js'; // ⬅︎ new path
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-auth.js';
import { getDoc } from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import { docWithTenant } from '../utils/firestoreHelpers.js';
import di from '../bootstrap/app.js';
import { AuthResilience } from './authResilience.js';
const { endpoints } = di;

const authTelemetry = di?.authTelemetry ?? di?.auth?.telemetry ?? null;

const authResilience = AuthResilience({
  auth,
  signInWithEmailAndPassword,
  telemetry: authTelemetry ?? undefined,
  logger: console,
  config: di?.authResilienceConfig ?? undefined,
});

const LoginService = {
  /**
   * Sign in with email and password.
   * @param {string} email
   * @param {string} password
   * @returns {Promise<import('firebase/auth').UserCredential>}
   */
  async signInUser(email, password) {
    try {
      await authReady;
    } catch (readyError) {
      console.warn('[LoginService] authReady failure (continuing)', readyError);
    }

    try {
      return await authResilience.signIn(email, password, {
        flow: 'PASSWORD',
        entrypoint: 'LoginService.signInUser',
      });
    } catch (error) {
      const code =
        (error &&
          typeof error === 'object' &&
          /** @type {{ code?: string }} */ (error).code) ||
        null;
      console.error('Login failed:', code ?? 'unknown-error', error);
      throw error;
    }
  },

  /**
   * Sign in with a linked Google account.
   *
   * @returns {Promise<import('firebase/auth').UserCredential>}
   */
  async signInWithGoogle() {
    try {
      await authReady;
    } catch (readyError) {
      console.warn('[LoginService] authReady failure (continuing)', readyError);
    }

    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');
    provider.setCustomParameters({ prompt: 'select_account' });

    try {
      return await signInWithPopup(auth, provider);
    } catch (error) {
      const code =
        (error &&
          typeof error === 'object' &&
          /** @type {{ code?: string }} */ (error).code) ||
        null;
      console.error('Google sign-in failed:', code ?? 'unknown-error', error);
      throw error;
    }
  },

  /**
   * Sign in with a linked Microsoft account.
   *
   * @returns {Promise<import('firebase/auth').UserCredential>}
   */
  async signInWithMicrosoft() {
    try {
      await authReady;
    } catch (readyError) {
      console.warn('[LoginService] authReady failure (continuing)', readyError);
    }

    const provider = new OAuthProvider('microsoft.com');
    provider.addScope?.('email');
    provider.addScope?.('profile');
    provider.setCustomParameters?.({ prompt: 'select_account' });

    try {
      return await signInWithPopup(auth, provider);
    } catch (error) {
      const code =
        (error &&
          typeof error === 'object' &&
          /** @type {{ code?: string }} */ (error).code) ||
        null;
      console.error(
        'Microsoft sign-in failed:',
        code ?? 'unknown-error',
        error
      );
      throw error;
    }
  },

  /**
   * Read Firestore `Users/{userId}` and store CRM connections in sessionStorage.
   * @param {string} userId
   * @returns {Promise<void>}
   */
  async storeConnectionStatus(userId) {
    const authDi = di.auth ?? {};
    const tenantStore = di.tenant;
    let tenantId = authDi.currentUser?.tenantId;

    if (!tenantId && typeof tenantStore?.getTenantId === 'function') {
      tenantId = tenantStore.getTenantId();
    }

    if (!tenantId) {
      if (typeof authDi.waitForLogin === 'function') {
        const user = await authDi.waitForLogin().catch(() => null);
        tenantId =
          user?.tenantId ??
          tenantStore?.getTenantId?.() ??
          auth.currentUser?.tenantId ??
          null;
      } else {
        tenantId = auth.currentUser?.tenantId ?? null;
      }
    }

    if (!tenantId) {
      await new Promise((resolve) => setTimeout(resolve, 250));
      tenantId =
        authDi.currentUser?.tenantId ??
        tenantStore?.getTenantId?.() ??
        auth.currentUser?.tenantId ??
        null;
    }

    if (!tenantId) {
      sessionStorage.setItem('hubspotConnected', 'false');
      sessionStorage.setItem('salesforceConnected', 'false');
      return { hubspotConnected: false, salesforceConnected: false };
    }

    try {
      const snap = await getDoc(docWithTenant(tenantId, `Users/${userId}`));
      if (snap.exists()) {
        const data = snap.data();
        const hubspotConnected = !!data.hubspotConnected;
        const salesforceConnected = !!data.salesforceConnected;
        sessionStorage.setItem('hubspotConnected', String(hubspotConnected));
        sessionStorage.setItem(
          'salesforceConnected',
          String(salesforceConnected)
        );
        return { hubspotConnected, salesforceConnected };
      }

      console.warn(
        'User document not found – continuing without connection info.'
      );
      sessionStorage.setItem('hubspotConnected', 'false');
      sessionStorage.setItem('salesforceConnected', 'false');
      return { hubspotConnected: false, salesforceConnected: false };
    } catch (err) {
      console.warn(
        'Unable to fetch connection statuses; defaulting to disconnected flags.',
        err
      );
      sessionStorage.setItem('hubspotConnected', 'false');
      sessionStorage.setItem('salesforceConnected', 'false');
      return { hubspotConnected: false, salesforceConnected: false };
    }
  },

  /**
   * Fetch the user's HubSpot Owner ID from Firestore.
   * @param {string} userId
   * @returns {Promise<string|null>}
   */
  async fetchHubspotOwnerId(userId) {
    try {
      const user = await di.auth.waitForLogin();
      const tenantId = user.tenantId;
      if (!tenantId) {
        console.error('Missing tenant ID');
        throw new Error('Missing tenant ID');
      }
      const snap = await getDoc(docWithTenant(tenantId, `Users/${userId}`));
      if (!snap.exists()) {
        console.error('No user document found for UID:', userId);
        return null;
      }
      const { hubspot_owner_id } = snap.data();
      console.log('HubSpot Owner ID:', hubspot_owner_id);
      return hubspot_owner_id;
    } catch (err) {
      console.error('Error fetching HubSpot Owner ID:', err);
      throw err;
    }
  },

  /**
   * Example: fetch accounts (or install URL) from the environment-based endpoint.
   * @param {string} ownerId
   * @returns {Promise<Object[]>}
   */
  async fetchAccountsFromBackend(ownerId) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User is not logged in');

      const token = await currentUser.getIdToken(/* forceRefresh= */ true);
      const url = `${endpoints.generateInstallUrlHubspot}&ownerId=${ownerId}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok)
        throw new Error(`Backend error ${res.status}: ${res.statusText}`);

      const json = await res.json();
      return json.results ?? [];
    } catch (err) {
      console.error('Error fetching accounts:', err);
      throw err;
    }
  },

  /**
   * Render the accounts in a simple <ul>.
   * @param {Object[]} accounts
   * @param {string}   containerId
   */
  renderAccounts(accounts, containerId = 'responseData') {
    const container = document.getElementById(containerId);
    if (!container) return;

    container.innerHTML = '<h2>Your Accounts</h2>';
    const ul = document.createElement('ul');

    accounts.forEach(({ properties }) => {
      const li = document.createElement('li');
      li.textContent = properties?.name ?? 'Unnamed Account';
      ul.appendChild(li);
    });

    container.appendChild(ul);
  },
};

export default LoginService;
